import React from 'react';
import styled from 'styled-components';
import { ThemeProvider } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import { tabletThemeDark } from '@/themes/tabletTheme';
import { getNotificationTemplate } from '@/graph/notificationTemplates';
import PatientHeader from './PatientHeader';
import PreOpPatientBedPicker from './preop/PreOpPatientBedPicker';
import PreOpPatientPriorityPicker from './preop/PreOpPatientPriorityPicker';
import PreOpProcedurePriorityPicker from './preop/PreOpProcedurePriorityPicker';
import PreOpPatientRoomPicker from './preop/PreOpPatientRoomPicker';
import PreOpPatientPostOpRoomPicker from './preop/PreOpPatientPostOpRoomPicker';
import PreOpPatientOptions from './preop/PreOpPatientOptions';
import PatientBedPicker from './PatientBedPicker';
import PatientPriorityPicker from './PatientPriorityPicker';
import ProcedurePriorityPicker from './ProcedurePriorityPicker';
import PatientRoomPicker from './PatientRoomPicker';
import PatientPostOpRoomPicker from './PatientPostOpRoomPicker';
import { PatientDelayReasonModal } from './components/modals/PatientDelayReasonModal';
import { PatientDischargeConfirmModal } from './components/modals/PatientDischargeConfirmModal';
import { PatientCancelVisitConfirmModal } from './components/modals/PatientCancelVisitConfirmModal';
import { PatientSmsConfirmModal } from './components/modals/PatientSmsConfirmModal';
import { DischargeBothLastPatientModal } from './components/modals/DischargeBothLastPatientModal';
import { CustomModal, TransferModal } from '../tablet/OperationRoomTabletV1';
import { usePatientScreen } from './hooks/usePatientScreen';
import { RouterlessModal } from '@/se/components/Modal';
import { CancelableForm, formatTime } from '@/components/inputs/timeEdit/TimeEditForm';
import { FormInput } from '@/components/entities/patient/patientJourney/PatientEventEdit';
import PatientOptions from './PatientOptions';
import Troubleshooting from '../tablet/Troubleshooting';
import type { PatientScreenProps } from './types/PatientScreenTypes';
import { PRE_OP } from '../../../entities/room/enums';
import { shouldPromptBeforeAction } from './delayReasonUtils';
import { patientEnteredTime } from '../tablet/utils';
import { formatDuration } from '@/util/duration';
import { useCurrentTime } from '@/util/timerHooks';

// Discharge modals
const DischargeLastPatient = CustomModal(`Send Physician daily recap message.`);
const DischargeLastAnesthesiologist = CustomModal(`Send Anesthesiologist daily recap message.`);

// Transfer modals
const TransferToChairModal = TransferModal(`Mark patient Out of Bed.`);
const TransferToPTModal = TransferModal(`Transfer patient to PT.`);
const PTCompleteModal = TransferModal(`Mark patient as PT Complete.`);

/**
 * PatientScreen - Patient detail view for PreOp/PACU/PostOp tablets
 *
 * Displays patient information and action buttons for nursing staff.
 * Handles bed assignment, priority, room transfers, and patient status updates.
 *
 * @architecture
 * - TypeScript with full type safety
 * - Functional component with React hooks
 * - Apollo Client hooks (no deprecated HOCs)
 * - Memory leak prevention via usePatientScreen hook
 * - Modular modal components
 * - Theme-aware styling (dark tablet theme)
 *
 * @realtime
 * - Subscription updates via Apollo cache
 * - Instant UI updates after mutations
 * - Patient list shows updated status badges
 */
export function PatientScreen({
  headerData,
  bodyData,
  room,
  roomType,
  showDelayReasons = false,
  patientId,
  orPacuPatients,
  orthopedic,
  pacuV2,
  patientPickupMessage,
  readyForFamilyMessage,
  dischargeModalV2 = false,
}: PatientScreenProps) {
  const { patient } = headerData;

  // Main screen state with memory leak fix
  const { activeModal, closeModal, handleAction, setActiveModal } = usePatientScreen(bodyData, {
    patient,
    room,
    showDelayReasons,
  });

  // Query notification templates to check if manual confirmation is needed
  const { data: physicianLastProcedure } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'PhysicianLastProcedure', channel: 'SMS', destination: 'Physician' },
  });
  const physicianLastProcedureManual =
    physicianLastProcedure?.getNotificationTemplate?.[0]?.sendingSetting === 'MANUAL';

  const { data: anesthesiologistLastProcedure } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'AnesthesiologistLastProcedure', channel: 'SMS', destination: 'Anesthesiologist' },
  });
  const anesthesiologistLastProcedureManual =
    anesthesiologistLastProcedure?.getNotificationTemplate?.[0]?.sendingSetting === 'MANUAL';

  // Current time for duration calculations
  const currentTime = useCurrentTime({
    interval: 60 * 1000,
    precision: 'minute',
    batteryOptimized: true,
  });

  // Calculate patient entered time for transfer modals
  const timeEntered = patient && room ? patientEnteredTime(patient as any, room) : undefined;
  const patientEnteredDuration = timeEntered ? formatDuration(timeEntered, currentTime, true) : undefined;

  // Extract state and features from bodyData
  const state = {
    isPatientReady: bodyData.isPatientReady,
    isReadyForAnesthesia: bodyData.isReadyForAnesthesia,
    isSpinalTimeout: bodyData.isSpinalTimeout,
    isReadyForSurgeon: bodyData.isReadyForSurgeon,
    isReadyToSeeFamilyPreOp: bodyData.isReadyToSeeFamilyPreOp,
    isHoldProcedure: bodyData.isHoldProcedure,
    isBlockNerve: bodyData.isBlockNerve,
    hasCaretakerConsent: bodyData.hasCaretakerConsent,
    isFamilyReady: bodyData.isFamilyReady,
    isReadyForPickupPACU: bodyData.isReadyForPickupPACU,
    isReadyForPickupPOSTOP: bodyData.isReadyForPickupPOSTOP,
  };

  const features = {
    assignBed: bodyData.assignBed,
    assignPriority: bodyData.assignPriority,
    markAsReady: bodyData.markAsReady,
    markAsReadyForAnesthesia: bodyData.markAsReadyForAnesthesia,
    markAsSpinalTimeout: bodyData.markAsSpinalTimeout,
    markAsReadyForSurgeon: bodyData.markAsReadyForSurgeon,
    dischargePatient: bodyData.dischargePatient,
    moveToOR: bodyData.moveToOR,
    moveToPostOp: bodyData.moveToPostOp,
    cancelVisit: bodyData.cancelVisit,
    holdProcedure: bodyData.holdProcedure,
    blockNerve: bodyData.blockNerve,
    markAsReadyToSeeFamilyPreOp: bodyData.markAsReadyToSeeFamilyPreOp,
    markAsFamilyReady: bodyData.markAsFamilyReady,
    markAsFamilyReadyPreOp: bodyData.markAsFamilyReadyPreOp,
    transferToChair: bodyData.transferToChair,
    transferToPT: bodyData.transferToPT,
    ptComplete: bodyData.ptComplete,
  };

  // Calculate SMS indicator - shows "Sends Message" label under buttons
  const notificationNumbers = (patient as any)?.notificationNumbers || '';

  const smsIndicator = notificationNumbers !== '' && bodyData.hasCaretakerConsent;

  return (
    <ThemeProvider theme={tabletThemeDark}>
      <Container>
        {/* Header with back button and patient info */}
        <PatientHeader
          room={room}
          roomType={roomType}
          patient={headerData.patient}
          goBack={headerData.goBack}
          hasNoteModule={bodyData.hasPatientCharting}
        />

        {/* Main content - patient action buttons */}
        <Content>
          {roomType === PRE_OP ? (
            <PreOpPatientOptions
              patient={patient}
              room={room}
              roomType={roomType}
              onSelect={handleAction}
              patientId={patientId}
              orPacuPatients={orPacuPatients}
              orthopedic={orthopedic}
              pacuV2={pacuV2}
              patientPickupMessage={patientPickupMessage}
              readyForFamilyMessage={readyForFamilyMessage}
              hasPreOpPriorityModule={bodyData.hasPreOpPriorityModule}
              hasNoteModule={bodyData.hasNoteModule}
              hasProcedureNoteModule={bodyData.hasProcedureNoteModule}
              smsIndicator={smsIndicator}
              {...state}
              {...features}
            />
          ) : (
            <PatientOptions
              patient={patient}
              room={room}
              roomType={roomType}
              onSelect={handleAction}
              patientId={patientId}
              orPacuPatients={orPacuPatients}
              orthopedic={orthopedic}
              pacuV2={pacuV2}
              patientPickupMessage={patientPickupMessage}
              readyForFamilyMessage={readyForFamilyMessage}
              hasPreOpPriorityModule={bodyData.hasPreOpPriorityModule}
              hasNoteModule={bodyData.hasNoteModule}
              hasProcedureNoteModule={bodyData.hasProcedureNoteModule}
              smsIndicator={smsIndicator}
              {...state}
              {...features}
            />
          )}
        </Content>
        {/* Full-screen pickers - rendered conditionally */}
        {activeModal === 'bedPicker' &&
          (roomType === PRE_OP ? (
            <PreOpPatientBedPicker
              room={room}
              beds={bodyData.beds}
              onAssignBed={bodyData.onAssignBed}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ) : (
            <PatientBedPicker
              room={room}
              beds={bodyData.beds}
              onAssignBed={bodyData.onAssignBed}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ))}

        {activeModal === 'priorityPicker' &&
          (roomType === PRE_OP ? (
            <PreOpPatientPriorityPicker
              priorityCounter={bodyData.priorityCounter}
              assignedPriority={patient?.priority || undefined}
              onAssignPriority={(priority: number) => bodyData.assignPriorityToSelectedPatient(String(priority))}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
              roomName={room.name}
            />
          ) : (
            <PatientPriorityPicker
              priorityCounter={bodyData.priorityCounter}
              assignedPriority={patient?.priority || undefined}
              onAssignPriority={(priority: number) => bodyData.assignPriorityToSelectedPatient(String(priority))}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ))}

        {activeModal === 'procedurePriorityPicker' &&
          (roomType === PRE_OP ? (
            <PreOpProcedurePriorityPicker
              procedureId={patient?.procedureData?.id || 0}
              assignedPriority={patient?.procedureData?.procedurePriority ?? undefined}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
              roomName={room.name}
            />
          ) : (
            <ProcedurePriorityPicker
              procedureId={patient?.procedureData?.id || 0}
              assignedPriority={patient?.procedureData?.procedurePriority ?? undefined}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ))}

        {/* Discharge flow - check for both, then physician, then anesthesiologist last patient */}
        {activeModal === 'dischargeConfirm' &&
          // Case 1: BOTH physician and anesthesiologist last patient
          ((patient as any)?.physicianLastPatient &&
          physicianLastProcedureManual &&
          (patient as any)?.anesthesiologistLastPatient &&
          anesthesiologistLastProcedureManual ? (
            <DischargeBothLastPatientModal
              physicianName={(patient as any)?.physicianData?.name}
              physicianPhone={(patient as any)?.physicianData?.notificationNumbers}
              anesthesiologistName={(patient as any)?.anesthesiologistData?.name}
              anesthesiologistPhone={(patient as any)?.anesthesiologistData?.phoneNumber}
              onConfirm={async (sendToPhysician, sendToAnesthesiologist) => {
                const needsDelayReason = shouldPromptBeforeAction({
                  patient,
                  room,
                  delayType: 'pacuOut',
                  showDelayReasons,
                });

                if (sendToPhysician && sendToAnesthesiologist) {
                  // Send to both
                  if (needsDelayReason) {
                    setActiveModal('dischargePatientBothLastDelayReason');
                  } else {
                    await bodyData.onDischargeBothLastPatient();
                    closeModal();
                  }
                } else if (sendToPhysician) {
                  // Send to physician only
                  if (needsDelayReason) {
                    setActiveModal('dischargePatientPhysicianLastDelayReason');
                  } else {
                    await bodyData.onDischargePhysicianLastPatient();
                    closeModal();
                  }
                } else if (sendToAnesthesiologist) {
                  // Send to anesthesiologist only
                  if (needsDelayReason) {
                    setActiveModal('dischargePatientAnesthesiologistLastDelayReason');
                  } else {
                    await bodyData.onDischargeAnesthesiologistLastPatient();
                    closeModal();
                  }
                } else {
                  // Send to neither - just discharge
                  if (needsDelayReason) {
                    setActiveModal('dischargePatientDelayReason');
                  } else {
                    await bodyData.onDischargePatient();
                    closeModal();
                  }
                }
              }}
              onCancel={closeModal}
            />
          ) : // Case 2: Physician last patient only
          (patient as any)?.physicianLastPatient && physicianLastProcedureManual ? (
            <DischargeLastPatient
              sendTo={`Message will be sent to ${(patient as any)?.physicianData?.name} at ${(patient as any)?.physicianData?.notificationNumbers?.split(',')}`}
              messageToSend="Hi {{physicianName}}, your last patient {{patientFullName}} was discharged at {{currentTime}}. Here are your analytics for the day: Average PIPO (Patient in Patient out): {{physicianPipoAvg}}, Average Surgery Time: {{physicianSurgeryTimeAvg}}, Average Closing Time: {{physicianClosingTimeAvg}}, Average Turnover: {{physicianTurnoverAvg}}."
              handleConfirm={async () => {
                if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
                  setActiveModal('dischargePatientPhysicianLastDelayReason');
                } else {
                  await bodyData.onDischargePhysicianLastPatient();
                  closeModal();
                }
              }}
              handleSecondaryConfirm={async () => {
                if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
                  setActiveModal('dischargePatientDelayReason');
                } else {
                  await bodyData.onDischargePatient();
                  closeModal();
                }
              }}
              handleCancel={closeModal}
              physicianLastPatient={(patient as any)?.physicianLastPatient}
            />
          ) : // Case 3: Anesthesiologist last patient only
          (patient as any)?.anesthesiologistLastPatient && anesthesiologistLastProcedureManual ? (
            <DischargeLastAnesthesiologist
              sendTo={`Message will be sent to ${(patient as any)?.anesthesiologistData?.name} at ${(patient as any)?.anesthesiologistData?.phoneNumber}`}
              messageToSend="Hi {{anesthesiologistName}}, your last patient {{patientFullName}} was discharged at {{currentTime}}. Here are your analytics for the day: Average Patient In to Anesthesia Start, Average Anesthesia Start to Complete."
              handleConfirm={async () => {
                if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
                  setActiveModal('dischargePatientAnesthesiologistLastDelayReason');
                } else {
                  await bodyData.onDischargeAnesthesiologistLastPatient();
                  closeModal();
                }
              }}
              handleSecondaryConfirm={async () => {
                if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
                  setActiveModal('dischargePatientDelayReason');
                } else {
                  await bodyData.onDischargePatient();
                  closeModal();
                }
              }}
              handleCancel={closeModal}
              physicianLastPatient={(patient as any)?.anesthesiologistLastPatient}
            />
          ) : // Case 4: Standard discharge
          dischargeModalV2 ? (
            <PatientDischargeConfirmModal
              patientName={(patient as any)?.fullName || (patient as any)?.firstName || 'M.'}
              onConfirm={async () => {
                if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
                  setActiveModal('dischargePatientDelayReason');
                } else {
                  await bodyData.onDischargePatient();
                  closeModal();
                }
              }}
              onCancel={closeModal}
            />
          ) : (
            React.createElement(CustomModal('Are you sure you want to discharge this patient?'), {
              handleConfirm: async () => {
                if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
                  setActiveModal('dischargePatientDelayReason');
                } else {
                  await bodyData.onDischargePatient();
                  closeModal();
                }
              },
              handleCancel: closeModal,
            })
          ))}

        {activeModal === 'cancelVisitConfirm' && (
          <PatientCancelVisitConfirmModal
            onConfirm={async _reason => {
              // TODO: Pass reason to cancel visit mutation
              await bodyData.onCancelVisit();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'moveToOR' &&
          (roomType === PRE_OP ? (
            <PreOpPatientRoomPicker
              onSelectRoom={bodyData.onMoveToOr}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ) : (
            <PatientRoomPicker
              onSelectRoom={bodyData.onMoveToOr}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ))}

        {activeModal === 'moveToPostOp' &&
          (roomType === PRE_OP ? (
            <PreOpPatientPostOpRoomPicker
              onSelectRoom={bodyData.onMoveToPostOp}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ) : (
            <PatientPostOpRoomPicker
              onSelectRoom={bodyData.onMoveToPostOp}
              onCancel={closeModal}
              goToStartPage={bodyData.goBack}
            />
          ))}

        {activeModal === 'dischargePatientDelayReason' && (
          <PatientDelayReasonModal
            delayType="pacuOut"
            onSubmit={async reason => {
              if (bodyData.editPatientPacuOutWarningDelayReason) {
                await bodyData.editPatientPacuOutWarningDelayReason({
                  variables: { id: patientId, pacuOutWarningDelayReason: reason },
                });
              }
              await bodyData.onDischargePatient();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'dischargePatientPhysicianLastDelayReason' && (
          <PatientDelayReasonModal
            delayType="pacuOut"
            onSubmit={async reason => {
              if (bodyData.editPatientPacuOutWarningDelayReason) {
                await bodyData.editPatientPacuOutWarningDelayReason({
                  variables: { id: patientId, pacuOutWarningDelayReason: reason },
                });
              }
              await bodyData.onDischargePhysicianLastPatient();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'dischargePatientAnesthesiologistLastDelayReason' && (
          <PatientDelayReasonModal
            delayType="pacuOut"
            onSubmit={async reason => {
              if (bodyData.editPatientPacuOutWarningDelayReason) {
                await bodyData.editPatientPacuOutWarningDelayReason({
                  variables: { id: patientId, pacuOutWarningDelayReason: reason },
                });
              }
              await bodyData.onDischargeAnesthesiologistLastPatient();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'dischargePatientBothLastDelayReason' && (
          <PatientDelayReasonModal
            delayType="pacuOut"
            onSubmit={async reason => {
              if (bodyData.editPatientPacuOutWarningDelayReason) {
                await bodyData.editPatientPacuOutWarningDelayReason({
                  variables: { id: patientId, pacuOutWarningDelayReason: reason },
                });
              }
              await bodyData.onDischargeBothLastPatient();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'transferToChairDelayReason' && (
          <PatientDelayReasonModal
            delayType="oob"
            onSubmit={async reason => {
              if (bodyData.editPatientOobWarningDelayReason) {
                await bodyData.editPatientOobWarningDelayReason({
                  variables: { id: patientId, oobWarningDelayReason: reason },
                });
              }
              await bodyData.onTransferToChair();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'transferToPTDelayReason' && (
          <PatientDelayReasonModal
            delayType="pt"
            onSubmit={async reason => {
              if (bodyData.editPatientPtWarningDelayReason) {
                await bodyData.editPatientPtWarningDelayReason({
                  variables: { id: patientId, ptWarningDelayReason: reason },
                });
              }
              await bodyData.onTransferToPT();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {activeModal === 'ptCompleteDelayReason' && (
          <PatientDelayReasonModal
            delayType="ptComplete"
            onSubmit={async reason => {
              if (bodyData.editPatientPtCompleteWarningDelayReason) {
                await bodyData.editPatientPtCompleteWarningDelayReason({
                  variables: { id: patientId, ptCompleteWarningDelayReason: reason },
                });
              }
              await bodyData.onPTComplete();
              closeModal();
            }}
            onCancel={closeModal}
          />
        )}

        {/* Transfer confirmation modals with duration display */}
        {activeModal === 'transferToChair' && (
          <TransferToChairModal
            sendTo={
              patientEnteredDuration && !room?.oobWarningThreshold ? (
                <>
                  Patient in PACU to Patient Out of Bed Duration <br />
                  {patientEnteredDuration}
                </>
              ) : (
                <>
                  Patient in PACU to Patient Out of Bed Duration
                  <br />
                  Actual: {patientEnteredDuration} | Target: {room?.oobWarningThreshold}m
                </>
              )
            }
            handleConfirm={async () => {
              if (shouldPromptBeforeAction({ patient, room, delayType: 'oob', showDelayReasons })) {
                setActiveModal('transferToChairDelayReason');
              } else {
                await bodyData.onTransferToChair();
                closeModal();
              }
            }}
            handleCancel={closeModal}
          />
        )}

        {activeModal === 'transferToPT' && (
          <TransferToPTModal
            sendTo={
              patientEnteredDuration && !room?.ptWarningThreshold ? (
                <>
                  Patient in PACU to Transfer to PT Duration <br />
                  {patientEnteredDuration}
                </>
              ) : (
                <>
                  Patient in PACU to Transfer to PT Duration
                  <br />
                  Actual: {patientEnteredDuration} | Target: {room?.ptWarningThreshold}m
                </>
              )
            }
            handleConfirm={async () => {
              if (shouldPromptBeforeAction({ patient, room, delayType: 'pt', showDelayReasons })) {
                setActiveModal('transferToPTDelayReason');
              } else {
                await bodyData.onTransferToPT();
                closeModal();
              }
            }}
            handleCancel={closeModal}
          />
        )}

        {activeModal === 'ptComplete' && (
          <PTCompleteModal
            sendTo={
              patientEnteredDuration && !room?.ptCompleteWarningThreshold ? (
                <>
                  Patient in PACU to PT Complete Duration <br />
                  {patientEnteredDuration}
                </>
              ) : (
                <>
                  Patient in PACU to PT Complete Duration
                  <br />
                  Actual: {patientEnteredDuration} | Target: {room?.ptCompleteWarningThreshold}m
                </>
              )
            }
            handleConfirm={async () => {
              if (shouldPromptBeforeAction({ patient, room, delayType: 'ptComplete', showDelayReasons })) {
                setActiveModal('ptCompleteDelayReason');
              } else {
                await bodyData.onPTComplete();
                closeModal();
              }
            }}
            handleCancel={closeModal}
          />
        )}

        {/* Orthopedic time edit modals */}
        {activeModal === 'editTransferToChair' && bodyData.onTransferToChairTimeSubmit && (
          <RouterlessModal title="Update Transfer to Chair Time" onClose={closeModal}>
            <CancelableForm
              autoFocus
              label="Update"
              onClose={closeModal}
              initialValue={{
                time: (patient as any)?.events?.transferredToChairAt
                  ? formatTime((patient as any).events.transferredToChairAt)
                  : formatTime(new Date(), 'HH:mm'),
              }}
              input={FormInput('Transfer to Chair Time')}
              onSubmit={async (value: { time: string }) => {
                await bodyData.onTransferToChairTimeSubmit!(value);
                closeModal();
              }}
              busy={false}
            />
          </RouterlessModal>
        )}

        {activeModal === 'editTransferToPT' && bodyData.onTransferToPTTimeSubmit && (
          <RouterlessModal title="Update Transfer to PT Time" onClose={closeModal}>
            <CancelableForm
              autoFocus
              label="Update"
              onClose={closeModal}
              initialValue={{
                time: (patient as any)?.events?.transferredToPTAt
                  ? formatTime((patient as any).events.transferredToPTAt)
                  : formatTime(new Date(), 'HH:mm'),
              }}
              input={FormInput('Transfer to PT Time')}
              onSubmit={async (value: { time: string }) => {
                await bodyData.onTransferToPTTimeSubmit!(value);
                closeModal();
              }}
              busy={false}
            />
          </RouterlessModal>
        )}

        {activeModal === 'editPTComplete' && bodyData.onPTCompleteTimeSubmit && (
          <RouterlessModal title="Update PT Complete Time" onClose={closeModal}>
            <CancelableForm
              autoFocus
              label="Update"
              onClose={closeModal}
              initialValue={{
                time: (patient as any)?.events?.ptCompleteAt
                  ? formatTime((patient as any).events.ptCompleteAt)
                  : formatTime(new Date(), 'HH:mm'),
              }}
              input={FormInput('PT Complete Time')}
              onSubmit={async (value: { time: string }) => {
                await bodyData.onPTCompleteTimeSubmit!(value);
                closeModal();
              }}
              busy={false}
            />
          </RouterlessModal>
        )}

        {/* SMS Confirmation Modals */}
        {activeModal === 'readyForFamilyConfirm' && (
          <PatientSmsConfirmModal
            title="Notify Family - Ready for Visit"
            message=""
            onConfirm={async () => {
              await bodyData.onMarkAsReady();
              closeModal();
            }}
            onCancel={closeModal}
            sendTo={
              bodyData.hasCaretakerConsent && readyForFamilyMessage
                ? `SMS will be sent to: ${(patient as any)?.notificationNumbers || ''}`
                : undefined
            }
            messageToSend={
              bodyData.hasCaretakerConsent && readyForFamilyMessage
                ? readyForFamilyMessage
                    .replaceAll('{{patientFullName}}', (patient as any)?.name || '')
                    .replaceAll('{{patientInitials}}', (patient as any)?.initials || '')
                : undefined
            }
          />
        )}

        {activeModal === 'readyForPickupConfirm' && (
          <PatientSmsConfirmModal
            title="Notify Family - Ready for Pickup"
            message=""
            onConfirm={async () => {
              await bodyData.onMarkFamilyReady();
              closeModal();
            }}
            onCancel={closeModal}
            sendTo={
              bodyData.hasCaretakerConsent && patientPickupMessage
                ? `SMS will be sent to: ${(patient as any)?.notificationNumbers || ''}`
                : undefined
            }
            messageToSend={
              bodyData.hasCaretakerConsent && patientPickupMessage
                ? patientPickupMessage
                    .replaceAll('{{patientFullName}}', (patient as any)?.name || '')
                    .replaceAll('{{patientInitials}}', (patient as any)?.initials || '')
                : undefined
            }
          />
        )}

        {/* Footer with help/support buttons */}
        <Troubleshooting roomName={room.name} />
      </Container>
    </ThemeProvider>
  );
}

const Container = styled.div`
  display: grid;
  background: ${props => props.theme.backgroundColor.string()};
  color: ${props => props.theme.textColor.string()};
  grid-template-rows: [header] min-content [content] auto;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
  height: 100vh;
  width: 100%;
  overflow-x: hidden;
`;

const Content = styled.div`
  grid-row: content;
  overflow-y: auto;
  overflow-x: hidden;
`;
